import {
  type MutationFunction,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import Api, { withAcl } from 'hooks/Api';
import useModal from 'ui/partials/Modal/useModal';
import { toastSuccess } from 'ui/partials/Toast';
import { removeEmpty, useAdminParams } from 'utils/helpers';
import { useProvider } from 'utils/providers/Provider';
import _constants from 'modules/account/dashboard/utils/constants';
import { useTour } from '@reactour/tour';
import partialConstants from 'ui/partials/utils/constants';
import { Lead } from 'utils/types';

import { LEAD_FORM } from '../forms/schema';
import c from '../utils/constants';

interface UseCreateLeadProps {
  disableHide?: boolean;
}

export default function useCreateLead({
  disableHide = false,
}: UseCreateLeadProps = {}) {
  const queryClient = useQueryClient();
  const { hideModal } = useModal();
  const { state } = useProvider();
  const { accountId, accountUserId } = useAdminParams();
  const { isOpen, setIsOpen } = useTour();
  const args = state;

  const mutationFn: MutationFunction<Lead, LEAD_FORM> = async (_payload) => {
    // Triggers a successful mutation without writing to the DB
    // Only used when a tour is opened
    if (isOpen) return setIsOpen(false);

    const api = new Api(args);

    const payload: Record<string, any> = removeEmpty(_payload);

    if (payload?.companyId) delete payload.companyName;

    const data = await api.post(
      await withAcl({
        account: {
          url: 'api/v1/contacts/leads',
          payload,
        },
        admin: {
          url: `api/v2/accounts/${accountId}/users/${accountUserId}/contacts/leads`,
          payload,
        },
      }),
    );

    return data;
  };

  return useMutation({
    mutationFn,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [c.LEADS, {}] });
      queryClient.invalidateQueries({ queryKey: [c.COMPANIES, {}] });
      queryClient.invalidateQueries({ queryKey: [_constants.LEADS_COUNT, {}] });
      queryClient.invalidateQueries({
        queryKey: [partialConstants.TAGS],
      });

      if (!disableHide) hideModal();

      toastSuccess('Successfully created lead');
    },
  });
}
